import React from 'react';

const TemplateCorporate = ({ invoice, seller }) => {
  const amountInWords = "Sixteen Thousand Five Hundred Only"; 
  const isInterState = invoice.sellerState !== invoice.placeOfSupply;

  return (
    <div className="p-12 w-[210mm] min-h-[297mm] mx-auto bg-white text-slate-800 shadow-xl border border-gray-200">
      
      <div className="flex justify-between items-start mb-12">
        <div className="flex items-center gap-4">
          <img src={seller.logo} alt="Corporate Logo" className="h-16 w-16 object-contain" />
          <div>
            <h1 className="text-2xl font-extrabold tracking-tighter text-blue-900 leading-none">
              {seller.name}
            </h1>
            <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest font-semibold">Corporate Headquarters</p>
          </div>
        </div>
        <div className="text-right">
          <div className="bg-blue-900 text-white px-4 py-1 inline-block mb-2 font-bold tracking-widest text-xs uppercase">
            Tax Invoice
          </div>
          <p className="text-sm font-bold"># {invoice.invoiceNo}</p>
          <p className="text-xs text-slate-500">Dated: {invoice.invoiceDate}</p>
        </div>
      </div>

      {/* 2. Addresses Section */}
      <div className="grid grid-cols-2 gap-12 mb-10 pb-8 border-b border-slate-100">
        <div>
          <h3 className="text-[10px] uppercase font-bold text-blue-900 mb-3 border-b border-blue-100 pb-1">Vendor Details</h3>
          <p className="text-sm font-bold">{seller.name}</p>
          <p className="text-xs leading-relaxed text-slate-600">{seller.address}</p>
          <p className="text-xs font-bold mt-2">GSTIN: <span className="font-normal">{seller.gstin}</span></p>
          <p className="text-xs font-bold">PAN: <span className="font-normal">{seller.gstin.substring(2, 12)}</span></p>
        </div>
        <div>
          <h3 className="text-[10px] uppercase font-bold text-blue-900 mb-3 border-b border-blue-100 pb-1">Client Details (Bill To)</h3>
          <p className="text-sm font-bold">{invoice.customerName}</p>
          <p className="text-xs leading-relaxed text-slate-600">{invoice.shippingAddress}</p>
          <p className="text-xs font-bold mt-2">GSTIN: <span className="font-normal">{invoice.customerGstin}</span></p>
          <p className="text-xs font-bold">Place of Supply: <span className="font-normal">{invoice.placeOfSupply}</span></p>
        </div>
      </div>

      {/* 3. Line Items Table */}
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-slate-50 text-[10px] uppercase font-bold text-slate-600 border-y border-slate-200">
            <th className="p-3">#</th>
            <th className="p-3">Item Description</th>
            <th className="p-3 text-center">HSN</th>
            <th className="p-3 text-right">Qty</th>
            <th className="p-3 text-right">Unit Price</th>
            <th className="p-3 text-right">Disc.</th>
            <th className="p-3 text-right">Total</th>
          </tr>
        </thead>
        <tbody className="text-sm divide-y divide-slate-100">
          {invoice.items.map((item, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              <td className="p-3 text-xs text-slate-400">{i + 1}</td>
              <td className="p-3 font-medium">
                {item.name}
                <div className="text-[10px] text-slate-400 font-normal">Standard Business Unit</div>
              </td>
              <td className="p-3 text-center text-xs text-slate-500">{item.hsn}</td>
              <td className="p-3 text-right">{item.qty} {item.unit}</td>
              <td className="p-3 text-right">₹{Number(item.rate).toLocaleString()}</td>
              <td className="p-3 text-right text-red-400">-₹{item.discount}</td>
              <td className="p-3 text-right font-semibold">
                ₹{(Number(item.qty) * Number(item.rate) - Number(item.discount)).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 4. Totals and Summary */}
      <div className="mt-8 flex justify-between">
        <div className="w-1/2">
          <div className="mb-6">
            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Total in Words</p>
            <p className="text-xs font-medium italic text-slate-600 uppercase tracking-tight">INR {amountInWords}</p>
          </div>
          <div className="bg-slate-50 p-4 border-l-4 border-blue-900">
            <h4 className="text-[10px] uppercase font-bold text-blue-900 mb-2">Remittance Instructions</h4>
            <div className="grid grid-cols-2 text-[11px] gap-y-1">
              <span className="text-slate-500">Beneficiary:</span> <span className="font-bold">{seller.bankDetails.accountName}</span>
              <span className="text-slate-500">Bank Name:</span> <span>{seller.bankDetails.bankName}</span>
              <span className="text-slate-500">Account No:</span> <span className="font-mono">{seller.bankDetails.accountNo}</span>
              <span className="text-slate-500">IFSC Code:</span> <span className="font-mono">{seller.bankDetails.ifsc}</span>
            </div>
          </div>
        </div>

        <div className="w-1/3">
          <div className="space-y-2 text-sm border-t-2 border-slate-900 pt-4">
            <div className="flex justify-between">
              <span className="text-slate-500">Taxable Subtotal</span>
              <span>₹{(invoice.total / 1.18).toFixed(2)}</span>
            </div>
            {isInterState ? (
               <div className="flex justify-between">
                <span className="text-slate-500">IGST (18%)</span>
                <span>₹{(invoice.total - (invoice.total / 1.18)).toFixed(2)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-slate-500">CGST (9%)</span>
                  <span>₹{((invoice.total - (invoice.total / 1.18)) / 2).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">SGST (9%)</span>
                  <span>₹{((invoice.total - (invoice.total / 1.18)) / 2).toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between font-bold text-xl text-blue-900 pt-2 border-t border-slate-200">
              <span>Grand Total</span>
              <span>₹{invoice.total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Legal Footer */}
      <div className="mt-20">
        <div className="flex justify-between items-end">
          <div className="w-1/2">
            <h4 className="text-[10px] uppercase font-bold text-slate-400 mb-2">Terms of Service</h4>
            <ol className="text-[9px] text-slate-400 list-decimal pl-3 space-y-1">
              {seller.terms.map((term, i) => <li key={i}>{term}</li>)}
            </ol>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-bold text-slate-800 mb-12 uppercase">For {seller.name}</p>
            <div className="border-t border-slate-300 w-48 text-[10px] text-slate-400 pt-1">
              Authorized Signatory
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCorporate;